import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Global logout handler that can be set by AuthContext 1
let globalLogoutHandler: (() => void) | null = null;

export const setGlobalLogoutHandler = (handler: () => void) => {
  globalLogoutHandler = handler;
};

// Preserve the real toast.error so we can restore it after login
const originalToastError = toast.error.bind(toast);

// Track whether session expiry has already been handled this session
let sessionExpired = false;

// The portal is the coordinator's only screen and she reads Hebrew; an
// English sentence here is the last thing she sees before being sent back
// to the login page.
function handleSessionExpired(message = 'תוקף ההתחברות פג. נא להתחבר מחדש.') {
  if (sessionExpired) return;
  sessionExpired = true;

  // Show the single "session expired" notification
  originalToastError(message);

  // Suppress all subsequent toast.error calls so page-level catch blocks
  // don't stack additional "Failed to load X" messages on top
  (toast as any).error = () => '';

  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');

  if (globalLogoutHandler) {
    try {
      globalLogoutHandler();
    } catch (logoutError) {
      console.error('Error during logout:', logoutError);
    }
  }
}

// Reset module-level state on logout so the next login attempt isn't blocked
export const resetApiState = () => {
  isRefreshing = false;
  failedQueue.forEach((prom) => prom.reject(new Error('Session reset')));
  failedQueue = [];
  lastActivityTime = Date.now();
  sessionExpired = false;
  (toast as any).error = originalToastError;
};

// Track last activity time for idle timeout
let lastActivityTime = Date.now();
// The coordinator does not sit in front of this screen continuously: a fax
// arrives, she types a referral, the phone rings, she comes back. Fifteen
// minutes logged her out mid-shift and took a half-filled form with it. Long
// enough for a real shift, short enough that an abandoned screen does not stay
// open all day (Ohad, 29/08).
//
// The comment this replaced said "1 minute, change to 15 for production" while
// the value was already 15 - the kind of stale note that gets somebody to
// "fix" it back down.
const IDLE_TIMEOUT_MS = 45 * 60 * 1000;

// Don't track activity via events - we'll update it only after successful idle checks
// This prevents the activity time from being updated during the check itself

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Add auth token to requests and check idle timeout
api.interceptors.request.use((config) => {
  // If session already expired, silently cancel further requests
  if (sessionExpired) {
    return Promise.reject(new Error('Session expired'));
  }

  const token = localStorage.getItem('token');

  // Skip idle timeout check for auth endpoints (login, refresh, forgot-password, reset-password)
  const isAuthEndpoint = config.url?.includes('/auth/login') ||
                         config.url?.includes('/auth/refresh') ||
                         config.url?.includes('/auth/forgot-password') ||
                         config.url?.includes('/auth/reset-password');

  if (token && !isAuthEndpoint) {
    config.headers.Authorization = `Bearer ${token}`;

    // Check if user has been idle for too long
    const currentTime = Date.now();
    const idleTime = currentTime - lastActivityTime;

    if (idleTime > IDLE_TIMEOUT_MS) {
      handleSessionExpired('ההתחברות הסתיימה עקב חוסר פעילות. נא להתחבר מחדש.');
      return Promise.reject(new Error('Session expired due to inactivity'));
    }

    // User is active, update last activity time for this request
    lastActivityTime = currentTime;
  } else if (token) {
    // For auth endpoints, just add the token without idle check
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Handle auth errors and token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Check if this is a 401 error and not already a retry
    // Skip session-expired handling for public endpoints that don't require auth
    const isPublicEndpoint = originalRequest.url?.startsWith('/public/');
    if (error.response?.status === 401 && !originalRequest._retry && !isPublicEndpoint) {
      // Check if the backend returned "Session expired due to inactivity"
      const errorDetail = (error.response?.data as any)?.detail;
      const isIdleTimeout = errorDetail === 'Session expired due to inactivity';

      // Check if idle timeout has been exceeded (client-side or server-side)
      const idleTime = Date.now() - lastActivityTime;

      if (idleTime > IDLE_TIMEOUT_MS || isIdleTimeout) {
        handleSessionExpired('ההתחברות הסתיימה עקב חוסר פעילות. נא להתחבר מחדש.');
        return Promise.reject(error);
      }

      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        resetApiState();
        handleSessionExpired();
        return Promise.reject(error);
      }

      try {
        console.log('Calling refresh endpoint...');
        // Attempt to refresh the token
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          { refresh_token: refreshToken },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        const { access_token, refresh_token: newRefreshToken } = response.data;

        // Update tokens in localStorage
        localStorage.setItem('token', access_token);
        localStorage.setItem('refreshToken', newRefreshToken);

        // Update the Authorization header
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        // Update last activity time since we successfully refreshed
        lastActivityTime = Date.now();

        // Process the queued requests
        processQueue(null, access_token);

        isRefreshing = false;

        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null);
        isRefreshing = false;
        handleSessionExpired();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;